import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { InputBox } from '../../FormComponent';
import ButtonComponent from '../../customButton';
import ScreenshotImage from '../ScreenshotImage';
import { resolveScreenshotUrl } from '../../../utils/screenshotUtils';
import ScreenshotImagePreviewModal from '../../shared/ScreenshotImagePreviewModal';
import moment from 'moment';
import CustomChip from '../../chips/CustomChip';
import ArgumentsComponent from '../../(Automation)/ReplayPage/components/ScriptDetail/ArgumentsComponent';
import { DO_NOTHING } from '../../../utils/commonfunctions';
import ExplorerView from '../../AssetsManagement/ExplorerView';
import { getExecutionFileDownloadUrl } from '../../../utils/api/executionFileApis';
import { STATUS } from '../../../utils/api/sharedfunction';
import { useSnackbar } from '../../../utils/useSnackbar';
import { getDisplayFileName } from '../../AssetsManagement/helpers';
import styles from '../../../styles/automation/results/resultsPanel.module.scss';
import explorerStyles from '../../../styles/assetsManagement/filesManager.module.scss';
import ErrorDetailsDisplay from './ErrorDetailsDisplay';
import { SKIP_ARGUMENTS, ONLY_CUSTOM_ARGUMENTS } from '../../(Automation)/constant';
import ToolOutput from '../../AutomationStudio/components/shared/ToolOutput';
import StepAgentResultTrace from '../../AutomationStudio/components/RightPanel/StepEditorView/AgentResult/StepAgentResultTrace';
import { mapStepWiseDynamicResultsToTraceTurns } from '../../AutomationStudio/components/RightPanel/StepEditorView/AgentResult/mapStepWiseDynamicResultsToTraceTurns';
import { LABELS } from '../../AutomationStudio/constants';

const NO_VALUE = 'Not Available';

/** Same visibility rules as Automation Studio `ArgumentsTab` / Replay `ArgumentsComponent`. */
function getVisibleArgKeys(args, customStep = false) {
    if (!args || typeof args !== 'object') return [];
    return Object.keys(args).filter(
        (k) => !SKIP_ARGUMENTS.includes(k) && !(ONLY_CUSTOM_ARGUMENTS.includes(k) && !customStep)
    );
}

function pickVisibleArgs(args, customStep = false) {
    const keys = getVisibleArgKeys(args, customStep);
    const out = {};
    for (const k of keys) out[k] = args[k];
    return out;
}

/** Playwright MCP uses browser_take_screenshot; product / schema use browser_screenshot. */
const MCP_TOOL_DISPLAY_ALIASES = { browser_take_screenshot: 'browser_screenshot' };

function getDisplayToolName(tool) {
    if (!tool) return tool;
    return MCP_TOOL_DISPLAY_ALIASES[tool] || tool;
}

/** Resolve map key when schema is registered under the canonical name. */
function resolveToolKeyForMcpMap(tool, map) {
    if (!tool || !map) return tool;
    if (map[tool]) return tool;
    const aliased = getDisplayToolName(tool);
    return map[aliased] ? aliased : tool;
}

/** Same idea as Studio: screenshot steps do not surface a dedicated arguments panel. */
const TOOLS_OMIT_ARGS_SECTION = new Set(['browser_screenshot', 'browser_take_screenshot']);

function formatArgLabelKey(key) {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
}

function formatArgValueForDisplay(val) {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
}

function ReadOnlyVisibleToolArguments({ args }) {
    const entries = Object.entries(args || {});
    return (
        <div className={styles.readOnlyToolArgList}>
            {entries.map(([key, val]) => (
                <div key={key} className={styles.readOnlyToolArgRow}>
                    <span className={styles.readOnlyToolArgKey}>{formatArgLabelKey(key)}</span>
                    <span className={styles.readOnlyToolArgVal}>{formatArgValueForDisplay(val)}</span>
                </div>
            ))}
        </div>
    );
}

function getPlaywrightAssertionCode(playwrightAssertion) {
    if (playwrightAssertion == null) return null;
    if (typeof playwrightAssertion === 'string') return playwrightAssertion;
    if (typeof playwrightAssertion === 'object' && playwrightAssertion.code) return playwrightAssertion.code;
    return null;
}

/** Dynamic / flattened Lambda steps carry automationPhase or ids like dynamic-mcp-0-0 */
function isDynamicExecutionStep(results) {
    if (!results) return false;
    if (results.automationPhase === 'mcp' || results.automationPhase === 'evaluation') return true;
    const id = results.id;
    return typeof id === 'string' && id.startsWith('dynamic-');
}

/** Same buckets as nlp `confidencePercentToLlmLabel` / TestReplayAgent LLM fallback. */
function confidenceNumberToQualitativeLabel(n) {
    if (n == null || typeof n !== 'number' || Number.isNaN(n)) return null;
    const clamped = Math.max(0, Math.min(100, n));
    if (clamped >= 90) return 'High';
    if (clamped >= 60) return 'Medium';
    return 'Low';
}

function formatAiConfidenceLabel(results) {
    const rawLabel =
        (typeof results?.llmConfidence === 'string' && results.llmConfidence.trim() && results.llmConfidence) ||
        (typeof results?.args?.llmConfidence === 'string' && results.args.llmConfidence.trim() && results.args.llmConfidence);
    if (rawLabel) {
        const word = rawLabel.trim().toLowerCase();
        const cap = word.charAt(0).toUpperCase() + word.slice(1);
        return `${cap} confidence`;
    }
    const n =
        typeof results?.confidence === 'number'
            ? results.confidence
            : typeof results?.args?.confidence === 'number'
              ? results.args.confidence
              : null;
    const qualitative = confidenceNumberToQualitativeLabel(n);
    if (qualitative) {
        return `${qualitative} confidence`;
    }
    return 'confidence';
}

// Error formatting functions (copied from ResultsPanel)
function formatPlaywrightError(rawError) {
    if (!rawError) return 'Assertion failed';
  
    // Strip ANSI color codes
    const cleanError = rawError.replace(/\u001b\[[0-9;]*m/g, '');
  
    // Parse the error into structured format
    const lines = cleanError.split('\n').filter(line => line.trim());
  
    const errorInfo = {
      assertion: '',
      locator: '',
      expected: '',
      received: '',
      timeout: '',
      elementDetails: '',
      callLog: []
    };
  
    // Extract key information
    lines.forEach(line => {
      if (line.includes('expect(') && line.includes(').')) {
        // Extract the assertion
        const match = line.match(/expect\((.*?)\)\.(.*?)(?:\((.*?)\))?/);
        if (match) {
          errorInfo.assertion = `expect(${match[1]}).${match[2]}${match[3] ? `(${match[3]})` : ''}`;
        }
      } else if (line.includes('Locator:')) {
        errorInfo.locator = line.replace('Locator:', '').trim();
      } else if (line.includes('Expected')) {
        errorInfo.expected = line.replace(/Expected.*?:/, '').trim();
      } else if (line.includes('Received')) {
        errorInfo.received = line.replace(/Received.*?:/, '').trim();
      } else if (line.includes('timeout:')) {
        errorInfo.timeout = line.replace('timeout:', '').trim();
      } else if (line.includes('locator resolved to')) {
        // Extract element info
        const elementMatch = line.match(/<([^>]+)>/);
        if (elementMatch) {
          errorInfo.elementDetails = elementMatch[0];
        }
      } else if (line.includes('waiting for') || line.includes('unexpected value')) {
        errorInfo.callLog.push(line.trim());
      }
      else {
        errorInfo.callLog.push(line.trim());
      }
    });
  
    return errorInfo;
}

function formatErrorForDisplay(errorInfo) {
    if (typeof errorInfo === 'string') {
      errorInfo = formatPlaywrightError(errorInfo);
    }
    const parts = [];
    if (errorInfo.assertion) {
      parts.push(`Assertion: ${errorInfo.assertion}`);
    }
    if (errorInfo.locator) {
      parts.push(`Locator: ${errorInfo.locator}`);
    }

    if (errorInfo.expected) {
      parts.push(`Expected: ${errorInfo.expected}`);
    }
  
    if (errorInfo.received) {
      parts.push(`Received: ${errorInfo.received}`);
    }
  
    if (errorInfo.timeout) {
      parts.push(`Timeout: ${errorInfo.timeout}`);
    }
  
    if (errorInfo.elementDetails) {
      parts.push(`Element: ${errorInfo.elementDetails}`);
    }
  
    if (errorInfo.callLog.length > 0) {
      parts.push(`Details:`);
      errorInfo.callLog.forEach(log => {
        parts.push(`   • ${log}`);
      });
    }
  
    return parts.join('\n');
}

// Mapping for xpath types to their display labels
const XPATH_TYPE_LABELS = {
    'xpath_playwright': 'Targeted Selector',
    'xpath_rules': 'Resilient Selector',
    'xpath_custom': 'Custom Selector'
};

// Reusable Box Component
const Box = ({ children, className = '' }) => {
    return <div className={`${styles.box} ${className}`}>{children}</div>;
};

// Status Badge Component
const StatusBadge = ({ status, text }) => {
    const isSuccess = status === 'passed' || status === 'success';
    return (
        <span className={`${styles.statusBadge} ${isSuccess ? styles.success : styles.failed}`}>
            {isSuccess ? (
                <CheckIcon className="icon-medium" />
            ) : (
                <XMarkIcon className="icon-medium" />
            )}
            {text}
        </span>
    );
};

// Common Section Component (for AI Playwright and XPath sections)
const SectionBox = ({ title, rightContent, children }) => {
    return (
        <Box className={styles.sectionBox}>
            <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>{title}</span>
                {rightContent}
            </div>
            <div className={styles.sectionDivider}></div>
            <div className={styles.sectionContent}>
                {children}
            </div>
        </Box>
    );
};

// Collapsible Section Component (only for Screenshot)
const CollapsibleSection = ({ title, children, defaultExpanded = false, rightContent = null }) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    return (
        <Box className={styles.collapsibleSection}>
            <div className={styles.collapsibleHeader} onClick={() => setIsExpanded(!isExpanded)}>
                <div className={styles.collapsibleLeft}>
                    <button className={styles.chevronBtn}>
                        {isExpanded ? (
                            <ChevronUpIcon className="icon-sm" />
                        ) : (
                            <ChevronDownIcon className="icon-sm" />
                        )}
                    </button>
                    <span className={styles.sectionTitle}>{title}</span>
                </div>
                {rightContent}
            </div>
            {isExpanded && <div className={styles.collapsibleContent}>{children}</div>}
        </Box>
    );
};

export default function ExecutionResultsPanel({
    selectedStep,
    executionResults,
    testSummary,
    mcpToolsList = [],
    mcpToolMap = {},
    executionFilesLoading = false,
    emptyStateMessage = null,
}) {
    
    // const results = null; 
    const results = selectedStep ?? null; 
    // Use testSummary prop if available, otherwise fall back to results.testSummary
    const summaryData = testSummary || results?.testSummary;
    const { showSnackbar } = useSnackbar();

    const handleExecutionFileDownload = useCallback(async (fileItem) => {
        try {
            const res = await getExecutionFileDownloadUrl({ executionFileId: fileItem.id });
            if (res.status !== STATUS.SUCCESS || !res.data?.downloadUrl) {
                showSnackbar(res.error || 'Failed to get download URL', 'error');
                return;
            }
            const { downloadUrl, fileName: apiFileName } = res.data;
            const downloadFileName = apiFileName || getDisplayFileName(fileItem);
            const blob = await fetch(downloadUrl, { credentials: 'omit' }).then((r) => r.blob());
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = downloadFileName;
            a.rel = 'noopener noreferrer';
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            showSnackbar('Failed to download file', 'error');
        }
    }, [showSnackbar]);

    const executionFileRowButtons = useMemo(() => [
        {
            name: 'Download',
            tooltip: 'Download file',
            icon: <ArrowDownTrayIcon className="icon" />,
            action: (row) => handleExecutionFileDownload(row),
            disabled: false,
        },
    ], [handleExecutionFileDownload]);

    const executionFilesMissingCount = useMemo(() => {
        if (!results?.executionFileIds?.length || !Array.isArray(results?.executionFiles)) return 0;
        const requested = results.executionFileIds.length;
        const returned = results.executionFiles.length;
        return requested > returned ? requested - returned : 0;
    }, [results?.executionFileIds, results?.executionFiles]);

    const totalExecutionFileCount = results?.executionFileIds?.length ?? results?.executionFiles?.length ?? 0;

    const customStepForToolArgs = results?.isCustomStep === true;
    const displayToolName = getDisplayToolName(results?.tool);
    const schemaToolKey = resolveToolKeyForMcpMap(results?.tool, mcpToolMap);
    const omitToolArgumentsSection =
        results?.tool &&
        results.tool !== 'evaluateTestStep' &&
        TOOLS_OMIT_ARGS_SECTION.has(displayToolName);

    const toolArgsVisibleKeys =
        results?.args && results?.tool && results.tool !== 'evaluateTestStep' && !omitToolArgumentsSection
            ? getVisibleArgKeys(results.args, customStepForToolArgs)
            : [];
    const toolArgsDisplayStep =
        results?.args &&
        results?.tool &&
        results.tool !== 'evaluateTestStep' &&
        !omitToolArgumentsSection &&
        toolArgsVisibleKeys.length > 0
            ? {
                  ...results,
                  args: pickVisibleArgs(results.args, customStepForToolArgs),
                  customStep: customStepForToolArgs,
                  tool: schemaToolKey,
              }
            : null;

    // Helper function to create selectors from executionStrategies
    const createSelectorsFromStrategies = () => {
        if (!selectedStep?.executionStrategies) return [];
        
        return selectedStep.executionStrategies
            .filter(strategy => strategy?.type && strategy.type.startsWith('xpath_'))
            .map((strategy, index) => {
                const selectorStatus = getSelectorStatus(strategy);
                return {
                id: strategy.type,
                label: XPATH_TYPE_LABELS[strategy.type] || strategy.description || `Selector ${index + 1}`,
                xpath: strategy.selector || '',
                    status: selectorStatus.status,
                    statusText: selectorStatus.text
                };
            });
    };
    
    // Helper function to format duration
    const formatDuration = (ms) => {
        if (!ms && ms !== 0) return 'N/A';
        if (ms >= 1000) {
            return `${(ms / 1000).toFixed(1)}s`;
        }
        return `${ms}ms`;
    };
    
    // Helper function to get step status
    const getStepStatus = (step) => {
        return step.passed ? 'passed' : 'failed';
    };

    const getAssertionStatus = (step) => {
        const pa = step?.playwrightAssertion;
        if (pa && typeof pa === 'object' && Object.prototype.hasOwnProperty.call(pa, 'passed')) {
            return pa.passed ? 'passed' : 'failed';
        }
        if (typeof pa === 'string') {
            return 'passed';
        }
        return 'failed';
    };

    
    // Helper function to get execution method
    const getExecutionMethod = (step) => {
        if (step.executedStrategy?.type) {
            return step.executedStrategy.type.replace('_', ' ').toUpperCase();
        }
        return 'Multiple Strategies';
    };
    
    // Helper function to get AI code status
    const getAICodeStatus = (step) => {
        const playwrightStrategy = step.executionStrategies?.find(s => s?.type === 'playwright_code');
        if (playwrightStrategy?.executionResult?.success) {
            return { status: 'success', text: 'Success' };
        }
        if (playwrightStrategy?.executionResult?.attempted === false) {
            return { status: 'pending', text: 'Not Attempted' };
        }
        return { status: 'failed', text: 'Failed' };
    };
    
    // Helper function to get selector status
    const getSelectorStatus = (strategy) => {
        if (strategy.executionResult?.success) {
            return { status: 'success', text: 'Success' };
        }
        if (strategy.executionResult?.attempted === false) {
            return { status: 'pending', text: 'Not Attempted' };
        }
        return { status: 'failed', text: 'Failed' };
    };
    
    // State for editable selectors
    const [editableSelectors, setEditableSelectors] = useState(createSelectorsFromStrategies());
    
    // State for screenshot URL
    const [screenshotUrl, setScreenshotUrl] = useState(null);
    const [screenshotLoading, setScreenshotLoading] = useState(false);
    
    const [imagePreviewOpen, setImagePreviewOpen] = useState(false);

    // Update selectors when selectedStep changes
    useEffect(() => {
        setEditableSelectors(createSelectorsFromStrategies());
    }, [selectedStep]);

    // Resolve screenshot URL when step changes
    useEffect(() => {
        const fetchScreenshot = async () => {
            if (!selectedStep?.screenshotPath) {
                setScreenshotUrl(null);
                setScreenshotLoading(false);
                return;
            }

            setScreenshotLoading(true);
            try {
                const { url } = await resolveScreenshotUrl(selectedStep.screenshotPath);
                setScreenshotUrl(url);
            } catch (error) {
                console.error('Error resolving screenshot URL:', error);
                setScreenshotUrl(null);
            } finally {
                setScreenshotLoading(false);
            }
        };

        fetchScreenshot();
    }, [selectedStep?.screenshotPath]);

    const handleScreenshotClick = () => {
        if (screenshotUrl) {
            setImagePreviewOpen(true);
        }
    };

    // Empty State - Show test summary even when no step is selected
    if (!results) {
        if (emptyStateMessage) {
            return (
                <div className={styles.emptyStateContainer}>
                    <div className={`${styles.emptyStateContent} ${styles.emptyStateFailureBox}`}>
                        <p className={styles.emptyStateFailureText}>{emptyStateMessage}</p>
                    </div>
                </div>
            );
        }
        return (
            <div className={styles.emptyStateContainer}>
                <div className={styles.emptyStateContent}>
                    <h3 className={styles.emptyTitle}>Select a Step to View Details</h3>
                    <p className={styles.emptyDescription}>Choose a step from the left to see its execution details, strategies, and results</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.resultsContainer}>
            {/* Step Execution Header */}
            <Box>
                <h3 className={styles.resultTitle}>Step {results?.stepNumber} Execution Result</h3>
                <p className={styles.resultDescription}>{results?.stepDescription || results?.description || 'Step Description'}</p>
                <div className={styles.metadataRow}>
                    {/* isDynamicExecutionStep(results) && */ results?.tool && (
                        <div className={styles.metadataItem}>
                            <span className={styles.metadataLabel}>Tool call:</span>
                            <span className={styles.metadataValue} /* style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} */>
                                {displayToolName}
                            </span>
                        </div>
                    )}
                    <div className={styles.metadataItem}>
                        <span className={styles.metadataLabel}>Status:</span>
                        <StatusBadge status={getStepStatus(results)} text={getStepStatus(results)} />
                    </div>
                    {
                        !isDynamicExecutionStep(results) &&
                        getPlaywrightAssertionCode(results?.playwrightAssertion) && (
                        <div className={styles.metadataItem}>
                            <span className={styles.metadataLabel}>Assertion Code:</span>
                            <StatusBadge status={getAssertionStatus(results)} text={getAssertionStatus(results)} />
                        </div>
                        )
                    }
                    <div className={styles.metadataItem}>
                        <span className={styles.metadataLabel}>Duration:</span>
                        <span className={styles.metadataValue}>{formatDuration(results?.duration)}</span>
                    </div>
                    {(results?.executedStrategy?.type === 'playwright_code' || results?.executedStrategy?.type?.startsWith('xpath_')) && (
                    <div className={styles.metadataItem}>
                        <span className={styles.metadataLabel}>Method:</span>
                            <CustomChip
                                label={results?.executedStrategy?.type === 'playwright_code' ? '🎭 PW Code' : '🔍 XPath'}
                                color={results?.executedStrategy?.type === 'playwright_code' ? '#dcfce7' : '#dbeafe'}
                                textColor={results?.executedStrategy?.type === 'playwright_code' ? '#166534' : '#1e40af'}
                                borderColor={results?.executedStrategy?.type === 'playwright_code' ? '#bbf7d0' : '#bfdbfe'}
                                size="small"
                                style={{ fontSize: '0.7rem', height: '20px' }}
                            />
                    </div>
                    )}
                    {results?.selfHealed && (
                    <div className={styles.metadataItem}>
                        <CustomChip
                            label="🔧 Healed"
                            color="#FEF3C7"
                            textColor="#92400E"
                            borderColor="#FCD34D"
                            size="small"
                            style={{ fontSize: '10px', height: '20px' }}
                        />
                    </div>
                    )}
                    {results?.isDynamic && (
                    <div className={styles.metadataItem}>
                        <span className={styles.dynamicChip}>Dynamic</span>
                    </div>
                    )}
                </div>
            </Box>

            {/* Error Box - Show if there's an error */}
            {results?.tool && results?.tool != 'evaluateTestStep' && results?.error && results?.error?.length > 0 && (
                <Box className={`${styles.aiStatusBox} ${styles.aiStatusError}`}>
                    <div className={styles.aiStatusHeader}>
                        <span className={styles.aiStatusIcon}>⚠️</span>
                        <span className={styles.aiStatusTitle}>Error</span>
                    </div>
                    <div className={styles.aiStatusContent}>
                        {results.error}
                    </div>
                </Box>
            )}

            {/* Result — Agent dynamic trace for isDynamic steps */}
            {results?.isDynamic /* && Array.isArray(results?.stepWiseDynamicResults) && results.stepWiseDynamicResults.length > 0 */ && (
                <CollapsibleSection title="Timeline" defaultExpanded={true}>
                    <StepAgentResultTrace
                        agentTraceTurns={mapStepWiseDynamicResultsToTraceTurns(results.stepWiseDynamicResults)}
                        isFromLambda
                        intent={results?.dynamicIntent}
                    />
                </CollapsibleSection>
            )}

            {/* Self-Healing Trace - shown when StepHealer recovered this step */}
            {results?.selfHealed && (
                <CollapsibleSection
                    title="🔧 Self-Healing Trace"
                    defaultExpanded={true}
                    rightContent={results.healAttempt != null ? <span className={styles.timestamp}>Attempt {results.healAttempt}</span> : null}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {results.healTool && (
                            <>
                                <div>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#78350F', marginBottom: 4 }}>Tool</div>
                                    <div style={{ padding: '6px 10px', background: '#fff8e6', border: '1px solid #FCD34D', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', color: '#92400E' }}>
                                        {results.healTool}
                                    </div>
                                </div>
                                {results.healArgs && (
                                    <div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: '#78350F', marginBottom: 4 }}>Arguments</div>
                                        <pre style={{ margin: 0, padding: '8px 10px', background: '#fff8e6', border: '1px solid #FCD34D', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', color: '#92400E', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {JSON.stringify(results.healArgs, null, 2)}
                                        </pre>
                                    </div>
                                )}
                                {results.toolCallTrace?.response && (
                                    <div>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: '#78350F', marginBottom: 4 }}>Response</div>
                                        <pre style={{ margin: 0, padding: '8px 10px', background: '#fff8e6', border: '1px solid #FCD34D', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', color: '#92400E', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 120, overflow: 'auto' }}>
                                            {results.toolCallTrace.response}
                                        </pre>
                                    </div>
                                )}
                            </>
                        )}
                        {!results.healTool && results.healMethod && (
                            <span style={{ fontSize: 13 }}><strong>Method:</strong> {results.healMethod}</span>
                        )}
                        {results.healedRef && (
                            <span style={{ fontSize: 13 }}><strong>Element ref:</strong> {results.healedRef}</span>
                        )}
                    </div>
                </CollapsibleSection>
            )}

            {/* Evaluation Step Handling - Similar to ResultsPanel */}
            {results?.tool && results?.tool == 'evaluateTestStep' && (
                <>
                    <Box>
                        {!isDynamicExecutionStep(results) &&
                        getPlaywrightAssertionCode(results.playwrightAssertion) && (
                            <div className={styles.infoRow}>
                                <div className={styles.label}>Assertion Code:</div>
                                <div className={`${styles.value}`}>
                                    <code className={styles.code}>{getPlaywrightAssertionCode(results.playwrightAssertion)}</code>
                                </div>
                            </div>
                        )}

                        {results?.stepDescription && (
                            <div className={styles.infoRow}>
                                <div className={styles.label}>Test Description:</div>
                                <div className={`${styles.value}`}>{results.stepDescription}</div>
                            </div>
                        )}
                        {results?.expectedOutcome && (
                            <div className={styles.infoRow}>
                                <div className={styles.label}>
                                    {results?.isDynamic ? LABELS.VERIFICATION_RECORDED_EXPECTED_OUTCOME : 'Expected Outcome:'}
                                </div>
                                <div className={`${styles.value}`}>{results.expectedOutcome}</div>
                            </div>
                        )}
                        {results?.isDynamic && results?.runTimeExpectedOutcome && (
                            <div className={styles.infoRow}>
                                <div className={styles.label}>{LABELS.VERIFICATION_RUNTIME_EXPECTED_OUTCOME}</div>
                                <div className={`${styles.value}`}>{results.runTimeExpectedOutcome}</div>
                            </div>
                        )}
                        {results?.actualOutcome && (
                            <div className={styles.infoRow}>
                                <div className={styles.label}>Actual Outcome:</div>
                                <div className={`${styles.value}`}>{results.actualOutcome}</div>
                            </div>
                        )}

                        {/* Locator is omitted for evaluateTestStep: assertion code is the source of truth; backend may auto-fill locator from last MCP action which is confusing here. */}

                        {results?.reason && results.tool === 'evaluateTestStep' && !results?.isPrimaryAIEvaluation && (
                            <div className={styles.infoRow}>
                                <div className={styles.label}>Reason:</div>
                                <div className={`${styles.value}`}>{results.reason}</div>
                            </div>
                        )}

                        {/* AI Fallback Evaluation - Clean Design */}
                        {results?.isPrimaryAIEvaluation && (results?.llmReason || results?.reason) && (
                            <Box className={styles.aiFallbackBox}>
                                <div className={styles.aiFallbackHeader}>
                                    <span className={styles.aiFallbackTitle}>AI Evaluation</span>
                                    <span className={styles.aiFallbackConfidence}>
                                        {formatAiConfidenceLabel(results)}
                                    </span>
                                </div>
                                <div className={styles.aiFallbackContent}>
                                    {/* <div className={styles.aiFallbackReason}>
                                        Original assertion failed, but AI analysis found:
                                    </div> */}
                                    <div className={styles.aiFallbackQuote}>
                                        {results.llmReason || results.reason}
                                    </div>
                                    {results.llmDuration && (
                                        <div className={styles.aiFallbackDuration}>
                                            AI evaluation took: {results.llmDuration >= 1000 ? 
                                                `${(results.llmDuration / 1000).toFixed(1)}s` : 
                                                `${results.llmDuration}ms`
                                            }
                                        </div>
                                    )}
                                </div>
                            </Box>
                        )}
                        
                        {/* AI Evaluation Status Messages - Enhanced Design */}
                        {results?.llmFallbackDisabled ? (
                            <Box className={`${styles.aiStatusBox} ${styles.aiStatusWarning}`}>
                                <div className={styles.aiStatusHeader}>
                                    <span className={styles.aiStatusIcon}>⚠️</span>
                                    <span className={styles.aiStatusTitle}>AI Evaluation Disabled</span>
                                </div>
                                <div className={styles.aiStatusContent}>
                                    The assertion failed. AI fallback evaluation is currently disabled. 
                                    Enable "AI Eval" toggle to use AI-powered re-evaluation for failed assertions.
                                </div>
                            </Box>
                        ) : results?.llmFallbackAttempted && results?.llmFallbackFailed ? (
                            <Box className={`${styles.aiStatusBox} ${styles.aiStatusError}`}>
                                <div className={styles.aiStatusHeader}>
                                    <span className={styles.aiStatusIcon}>⚠️</span>
                                    <span className={styles.aiStatusTitle}>AI Fallback Also Failed</span>
                                </div>
                                <div className={styles.aiStatusContent}>
                                    Both the saved assertion and AI-based evaluation determined this step failed.
                                </div>
                            </Box>
                        ) : null}

                        
                        {/* Error Details for Failed Assertions */}
                        {!results?.passed && results?.error && (
                            <div className={styles.infoRow}>
                                <div className={styles.label}>Error Details:</div>
                                <ErrorDetailsDisplay errorInfo={results.error} />
                            </div>
                        )}
                    </Box>
                </>
            )}

            {/* AI Playwright Code Section - Only render if playwright code exists */}
            {results?.executionStrategies?.find(strategy => strategy?.type === 'playwright_code')?.code && (
            <SectionBox
                title="AI Playwright Code"
                rightContent={<StatusBadge status={getAICodeStatus(results).status} text={getAICodeStatus(results).text} />}
            >
                <div className={styles.codeBlock}>
                    <code className={styles.code}>
                        {results?.executionStrategies?.find(
                            strategy => strategy?.type === 'playwright_code'
                        )?.code}
                    </code>
                </div>
            </SectionBox>
            )}

            {/* XPath Fallback Selectors Section - Only render if xpath strategies exist */}
            {editableSelectors.length > 0 && (
                <SectionBox
                    title="XPath Fallback Selectors"
                    rightContent={<StatusBadge status="success" text={`${editableSelectors.length} Selector${editableSelectors.length > 1 ? 's' : ''}`} />}
                >
                    <div className={styles.selectorsContainer}>
                        {editableSelectors.map((selector, index) => (
                            <Box key={selector.id} className={styles.selectorItem}>
                                <div className={styles.selectorHeader}>
                                    <span className={styles.selectorLabel}>
                                        {index + 1}. {selector.label}
                                    </span>
                                    <StatusBadge status={selector.status} text={selector.statusText} />
                                </div>
                                <InputBox
                                    readOnly={true}
                                    value={selector.xpath || 'No selector available'}
                                    onChange={DO_NOTHING}
                                    placeholder="XPath or CSS selector"
                                    size="small"
                                    customClassName={styles.selectorInput}
                                />
                            </Box>
                        ))}
                    </div>
                </SectionBox>
            )}

            {/* Tool Arguments — Studio filters; screenshot tools omit this section; fallback = labeled rows not raw JSON */}
            {results?.tool &&
                results?.tool !== 'evaluateTestStep' &&
                results?.args &&
                Object.keys(results.args).length > 0 &&
                !omitToolArgumentsSection && (
                <CollapsibleSection
                    title="Tool Arguments"
                    defaultExpanded={false}
                >
                    <div className={styles.toolArgBox}>
                        {toolArgsVisibleKeys.length === 0 ? (
                            <p
                                className={styles.emptyDescription}
                                style={{ color: '#64748b', fontSize: 13, margin: 0 }}
                            >
                                No arguments to show for this step. Internal fields (such as browser refs) are not
                                displayed.
                            </p>
                        ) : mcpToolMap && schemaToolKey && mcpToolMap[schemaToolKey] ? (
                            <ArgumentsComponent
                                key={results?.id}
                                selectedStep={toolArgsDisplayStep}
                                mcpToolMap={mcpToolMap}
                                readOnly={true}
                            />
                        ) : toolArgsDisplayStep ? (
                            <ReadOnlyVisibleToolArguments args={toolArgsDisplayStep.args} />
                        ) : null}
                    </div>
                </CollapsibleSection>
            )}

            {/* Execution Files Section - when step has executionFileIds and files were fetched */}
            {(results?.executionFileIds?.length > 0 || (results?.executionFiles && results.executionFiles.length > 0)) && (
                <CollapsibleSection
                    title="Execution Files"
                    defaultExpanded={true}
                    rightContent={executionFilesLoading ? (
                        <span className={styles.timestamp}>Loading...</span>
                    ) : totalExecutionFileCount > 0 ? (
                        <span className={styles.timestamp}>
                            {totalExecutionFileCount} file{totalExecutionFileCount !== 1 ? 's' : ''}
                        </span>
                    ) : null}
                >
                    {executionFilesLoading ? (
                        <div className={styles.screenshotPlaceholder}>
                            <div className={styles.placeholderIcon}>⏳</div>
                            <span className={styles.placeholderText}>Loading files...</span>
                        </div>
                    ) : results?.executionFiles?.length > 0 ? (
                        <>
                            {executionFilesMissingCount > 0 && (
                                <div className={styles.executionFilesMissingNotice}>
                                    <span className={styles.executionFilesMissingIcon}>📄</span>
                                    <span className={styles.executionFilesMissingText}>
                                        {executionFilesMissingCount} file{executionFilesMissingCount !== 1 ? 's' : ''} not found (deleted or removed)
                                    </span>
                                </div>
                            )}
                            <div className={explorerStyles.explorerWrap}>
                                <ExplorerView
                                    files={results.executionFiles}
                                    rowActionButtons={executionFileRowButtons}
                                />
                            </div>
                        </>
                    ) : (
                        <div className={styles.screenshotPlaceholder}>
                            <div className={styles.placeholderIcon}>📄</div>
                            <span className={styles.placeholderText}>No files found</span>
                            <span className={styles.placeholderHint}>Files may have been deleted or removed</span>
                        </div>
                    )}
                </CollapsibleSection>
            )}

            {/* Screenshot Section */}
            <CollapsibleSection
                title="Screenshot"
                rightContent={<span className={styles.timestamp}>{results?.timestamp ? moment(results.timestamp).format('MMM DD, YYYY hh:mm A') : 'N/A'}</span>}
            >
                {results?.screenshotPath ? (
                    screenshotLoading ? (
                        <div className={styles.screenshotPlaceholder}>
                            <div className={styles.placeholderIcon}>⏳</div>
                            <span className={styles.placeholderText}>Loading Screenshot...</span>
                        </div>
                    ) : screenshotUrl ? (
                    <ScreenshotImage
                            src={screenshotUrl}
                            rawPath={results?.screenshotPath || selectedStep?.screenshotPath}
                            alt={`Step ${results?.stepNumber} screenshot`}
                            onClick={handleScreenshotClick}
                        size="large"
                        showOverlay={true}
                        clickable={true}
                        borderStyle="solid"
                    />
                    ) : (
                        <div className={styles.screenshotPlaceholder}>
                            <div className={styles.placeholderIcon}>❌</div>
                            <span className={styles.placeholderText}>Screenshot Not Available</span>
                        </div>
                    )
                ) : (
                    <div className={styles.screenshotPlaceholder}>
                        <div className={styles.placeholderIcon}>📷</div>
                        <span className={styles.placeholderText}>No Screenshot Available</span>
                    </div>
                )}
            </CollapsibleSection>

            {/* Tool Output — collapsed by default for browser_evaluate / browser_network_requests / browser_console_messages */}
            <div style={{ marginTop: 12 }}>
                <ToolOutput step={results} stepDetails={results} />
            </div>

            {/* Image Preview Modal */}
            <ScreenshotImagePreviewModal
                open={imagePreviewOpen}
                onClose={() => setImagePreviewOpen(false)}
                imageUrl={screenshotUrl}
                fileName={`Step ${results?.stepNumber} Screenshot`}
                rawPath={results?.screenshotPath || selectedStep?.screenshotPath}
            />
        </div>
    );
}